import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Upload, Loader2, X, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 5 * 1024 * 1024; // 5MB

/**
 * ImageUpload — uploads an image to Cloudinary via signed backend request.
 * Props:
 *   value: current image URL (string)
 *   onChange: (url: string) => void
 *   folder: cloudinary folder (must start with allowed prefix; backend validates)
 *   label: visible label
 *   aspect: optional CSS aspect ratio for preview (default "aspect-video")
 */
export const ImageUpload = ({
  value,
  onChange,
  folder,
  label = 'Imagem',
  aspect = 'aspect-video',
  maxWidth = 'max-w-xs',
  testId,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Arquivo precisa ser uma imagem');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Imagem deve ter no máximo 5MB');
      return;
    }
    setUploading(true);
    try {
      const sigRes = await axios.get(`${BACKEND_URL}/api/cloudinary/signature`, {
        params: { folder },
        withCredentials: true,
      });
      const { signature, timestamp, cloud_name, api_key, folder: sigFolder } = sigRes.data;

      const form = new FormData();
      form.append('file', file);
      form.append('api_key', api_key);
      form.append('timestamp', timestamp);
      form.append('signature', signature);
      form.append('folder', sigFolder);

      const up = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: form }
      );
      const upJson = await up.json();
      if (!up.ok) {
        const msg = upJson?.error?.message || `Erro Cloudinary (${up.status})`;
        throw new Error(msg);
      }
      const url = upJson?.secure_url;
      if (!url) throw new Error('Resposta sem URL');
      onChange(url);
      toast.success('Imagem enviada!');
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.error?.message ||
        err.message;
      toast.error(typeof detail === 'string' ? detail : 'Falha ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {value ? (
        <div className={`relative w-full ${maxWidth} ${aspect} rounded-lg overflow-hidden border border-slate-200 bg-slate-50`}>
          <img
            src={value}
            alt={label}
            className="w-full h-full object-contain"
            data-testid={testId ? `${testId}-preview` : undefined}
          />
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              type="button"
              size="icon"
              variant="secondary"
              onClick={handlePick}
              disabled={uploading}
              className="h-8 w-8"
              data-testid={testId ? `${testId}-replace-btn` : undefined}
              title="Substituir"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              onClick={() => onChange('')}
              disabled={uploading}
              className="h-8 w-8"
              data-testid={testId ? `${testId}-remove-btn` : undefined}
              title="Remover"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handlePick}
          disabled={uploading}
          className={`w-full ${maxWidth} ${aspect} rounded-lg border-2 border-dashed border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-100 transition-colors flex flex-col items-center justify-center gap-1.5 text-slate-500`}
          data-testid={testId ? `${testId}-upload-btn` : undefined}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-xs">Enviando...</span>
            </>
          ) : (
            <>
              <ImageIcon className="h-5 w-5" />
              <span className="text-xs font-medium">Clique para enviar</span>
              <span className="text-[10px]">PNG, JPG até 5MB</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};
